import {useCallback, useContext, useEffect, useState} from "react";
import {Box, BoxProps, Container, SelectChangeEvent, Stack, Typography} from "@mui/material";
import Grid from '@mui/material/Grid2';
import {
  CONTEXT_ACTION_TYPE,
  DataStoreContext,
  DataStoreType,
  REGISTRATION_ACTION_TYPE
} from "../../contexts/WorkContext";
import SearchForm from "../forms/SearchForm";
import SortOrder, {useSortOrder} from "../forms/SortOrder";
import useConfirmDialog from "../../../../components/ConfirmDialog";
import {FormWorkSearchCond, workAttrList} from "../../types/workForm";
import {findWork, FindWorkResponseData} from "../../../../utils/awsAmplifyUtil";
import {handleInputChangeSelect} from "../../../../utils/formUtil";
import WorkListItem from "../WorkListItem";
import Pjdb1Button from "../../../../components/inputs/Pjdb1Button";
import {excludeClassesWithChildren} from "../../utils/workUtil";
import {WorkSortOrderValue} from "../../../../constants/constants";
import {canRegisterWork} from "../forms/RegisterForm";
import {canRegisterInstanceAndModel} from "../forms/RegisterInstanceAndModelForm";
import useFetchAllQuery, {FetchMessage, STATUSES, useFetchAllQueryHelper} from "../../../../hooks/useFetchAllQuery";
import {WorkClassDocument} from "../../../../types/pj1dbApiWork";
import useRepresentativeModel from "../../hooks/useRepresentativeModel";

type NextStep = (payload?: Partial<DataStoreType['workRegistrationPage']>) => void;

export interface SelectWorkProps {
  nextStep: NextStep;
}

export default function SelectWork(props: SelectWorkProps) {
  const {state, dispatch} = useContext(DataStoreContext);
  const {sortWorkList} = useSortOrder();
  const [searchCond, setSearchCond] = useState<FormWorkSearchCond>(state.workRegistrationPage.selection.searchCond);
  const convertWorkClasses = useCallback((data: DataStoreType['workRegistrationPage']['selection']['workClasses'], order: WorkSortOrderValue) => sortWorkList(
    excludeClassesWithChildren(data.map(workClassResult => Object.values(workClassResult.items)).flat()),
    order
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ), []);
  const [workClasses, setWorkClasses] = useState<WorkClassDocument[]>(
    convertWorkClasses(state.workRegistrationPage.selection.workClasses, state.workRegistrationPage.selection.searchCond.sortOrder)
  );
  const [selectedWork, setSelectedWork] = useState<DataStoreType['workRegistrationPage']['selection']['selectedWork']>(state.workRegistrationPage.selection.selectedWork);
  const selectedWorkStyle: BoxProps['sx'] = {
    outlineColor: (theme) => theme.palette.primary.light,
    outlineStyle: "solid",
    outlineWidth: 6,
    borderRadius: 1
  };
  const {openConfirmDialog: openNewFormDialog, ConfirmDialog: NewFormDialog} = useConfirmDialog();
  const {openConfirmDialog: openAddFormDialog, ConfirmDialog: AddFormDialog} = useConfirmDialog();
  const {status, data, fetchAll} = useWorkClasses({searchCond});
  const {searchStatus} = useFetchAllQueryHelper({
    onChange: (newData, newStatus) => {
      const newWorkClasses = convertWorkClasses(newData, state.workRegistrationPage.selection.searchCond.sortOrder);
      const newSearchCond = {...searchCond, listCount: newWorkClasses.length};
      setWorkClasses(newWorkClasses);
      setSearchCond(newSearchCond);
      dispatch({
        type: CONTEXT_ACTION_TYPE.WORK_REGISTRATION,
        payload: {
          ...state.workRegistrationPage,
          selection: {
            ...state.workRegistrationPage.selection,
            workClasses: newData,
            searchStatus: newStatus,
            searchCond: newSearchCond
          }
        } satisfies DataStoreType['workRegistrationPage']
      });
    },
    data,
    status,
    initialStatus: state.workRegistrationPage.selection.searchStatus,
  });

  const toggleWork = (work: WorkClassDocument) => {
    const isUnselect = selectedWork?.attr[workAttrList.classId.key] === work.attr[workAttrList.classId.key];
    const newSelectedWork = isUnselect ? null : work;
    dispatch({
      type: CONTEXT_ACTION_TYPE.WORK_REGISTRATION,
      payload: {
        ...state.workRegistrationPage,
        selection: {
          ...state.workRegistrationPage.selection,
          selectedWork: newSelectedWork
        }
      } satisfies DataStoreType['workRegistrationPage']
    });

    setSelectedWork(newSelectedWork);
  }

  const changeSortOrder = (event: SelectChangeEvent<WorkSortOrderValue>) => {
    handleInputChangeSelect<FormWorkSearchCond>(event, "sortOrder", searchCond, setSearchCond);
    const newSortOrder = event.target.value as WorkSortOrderValue;
    setWorkClasses(sortWorkList(workClasses, newSortOrder));
    dispatch({
      type: CONTEXT_ACTION_TYPE.WORK_REGISTRATION,
      payload: {
        ...state.workRegistrationPage,
        selection: {
          ...state.workRegistrationPage.selection,
          searchCond: {
            ...searchCond,
            sortOrder: newSortOrder,
          },
        }
      } satisfies DataStoreType['workRegistrationPage']
    });
  }

  const {models, isLoading } = useRepresentativeModel({
    workClasses,
    acquiredRepresentativeModels: state.workListPage.workModels,
  });
  useEffect(() => {
    dispatch({
      type: CONTEXT_ACTION_TYPE.WORK_REGISTRATION, payload: {
        ...state.workRegistrationPage,
        selection: {
          ...state.workRegistrationPage.selection,
          workModels: models,
        },
      } satisfies DataStoreType['workRegistrationPage']
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [models]);

  return (
    <>
      <SearchForm
        isLoading={searchStatus === STATUSES.FETCHING}
        searchCond={searchCond} setSearchCond={setSearchCond}
        onSubmit={(e) => {
          e.preventDefault();
          fetchAll().catch(console.error);
        }}
      />

      <Stack gap={4}>
        <Container
          sx={{p: 0, display: "flex", flexDirection: "row", flexWrap: "wrap"}}
          maxWidth={false}
        >
          <Grid container alignItems="end" sx={{mt: 2, flexWrap: "nowrap"}} size={{xs: 12}}>
            <Grid size="grow">
              <Typography variant="subtitle1" sx={{fontWeight: "bold"}}>
                検索結果一覧
              </Typography>
            </Grid>
            <Grid size="auto">
              <Grid container alignItems="center" spacing={2} sx={{flexWrap: "nowrap"}}>
                <Grid>
                  <Box component="span">
                    {searchCond.listCount > 0 ? `${searchCond.listCount.toLocaleString()}件` : ""}
                  </Box>
                </Grid>
                <Grid>
                  <Box component="span">
                    <SortOrder value={searchCond.sortOrder} onChange={changeSortOrder}/>
                  </Box>
                </Grid>
              </Grid>
            </Grid>
          </Grid>

          <Grid container alignItems="stretch" spacing={2} sx={{mt: 2}}>
            {workClasses.map(row => (
              <Grid
                key={row.attr[workAttrList.classId.key]}
                onClick={() => toggleWork(row)}>
                <Box boxSizing='border-box'
                     height='100%'
                     sx={selectedWork?.attr[workAttrList.classId.key] === row.attr[workAttrList.classId.key] ? selectedWorkStyle : {}}>
                  <WorkListItem work={row} representativeModels={state.workRegistrationPage.selection.workModels} isLoading={isLoading}/>
                </Box>
              </Grid>
            ))}
          </Grid>

          <FetchMessage
            data={workClasses}
            status={searchStatus}
            idleMessage={
              <Message>
                既存の対象物を追加登録する場合：対象物を検索して選択後に追加登録してください。<br/>
                新規の対象物を登録する場合：新規登録してください。<br/>
                ※対象物の別姿勢モデルの追加登録は登録済み対象物一覧から実施してください。
              </Message>
            }
          />
        </Container>


        <Box sx={{display: "flex", justifyContent: "center", width: "100%", gap: 4}}>
          <Pjdb1Button
            label="対象物を新規登録" variant="contained" color="info"
            disabled={!canRegisterWork()}
            onClick={() => {
              openNewFormDialog().then((result) => {
                if (result === "confirm") {
                  props.nextStep({
                    input: {
                      ...state.workRegistrationPage.input,
                      actionType: REGISTRATION_ACTION_TYPE.NEW,
                    },
                  });
                }
              }).catch(console.error);
            }}/>
          <Pjdb1Button
            label="既存の対象物に追加登録" variant="contained" color="primary"
            onClick={() => {
              openAddFormDialog().then((result) => {
                if (result === "confirm") {
                  props.nextStep({
                    input: {
                      ...state.workRegistrationPage.input,
                      actionType: REGISTRATION_ACTION_TYPE.REGISTER_INSTANCE_AND_MODEL,
                    }
                  });
                }
              }).catch(console.error);
            }}
            disabled={!selectedWork || !canRegisterInstanceAndModel()}/>
        </Box>
      </Stack>


      <NewFormDialog title="対象物の重複登録確認"
                     message="同じ対象物がすでに登録されていないか確認してください。"
                     confirmButton="はい"
                     cancelButton="いいえ"
                     confirmButtonStyle="info"/>
      <AddFormDialog title="選択した対象物の確認"
                     message="選択された対象物に間違いがないか確認してください。"
                     confirmButton="はい"
                     cancelButton="いいえ"
                     confirmButtonStyle="primary"/>
    </>
  )
}

function Message({children}: { children: React.ReactNode }) {
  return (
    <Box sx={{my: 3, width: "100%", textAlign: "center",}}>
      <Typography variant="subtitle1" sx={{fontWeight: "bold"}}>{children}</Typography>
    </Box>
  );
}

function useWorkClasses({searchCond}: { searchCond: FormWorkSearchCond }) {
  const queryResult = useFetchAllQuery<FindWorkResponseData<'class'>>({
    queryFn: (query) => {
      const params = {
        version: "work-user",
        queryAttr: {
          ...(searchCond.keyword === "" ? {} : {
            $or: [
              {[workAttrList.nameJp.key]: {"$regex": searchCond.keyword, "$options": "i"}},
              {[workAttrList.nameEn.key]: {"$regex": searchCond.keyword, "$options": "i"}},
              {[workAttrList.code.key]: {"$regex": searchCond.keyword, "$options": "i"}},
              {[workAttrList.manufacturer.key]: {"$regex": searchCond.keyword, "$options": "i"}},
            ]
          }),
        },
      };

      return findWork('class', params, query);
    },
    needToFetchMore: (data, limit) => Object.entries(data?.items ?? {}).length === limit,
  });

  return queryResult;
}
